/* Horizontal scrolling for the product rows, on every platform.

   The rows are `overflow-x` with the scrollbar hidden. That is fine with a
   touchscreen or a Mac trackpad, which both produce real horizontal gestures,
   but a standard Windows wheel mouse emits only a vertical delta and there is
   no visible scrollbar to drag — so those rows could not be scrolled at all.

   This deliberately does NOT translate vertical wheel input into horizontal
   scrolling. An earlier version did, and it hijacked ordinary vertical
   scrolling on a Mac: a trackpad flick is vertical-dominant too, and it emits
   dozens of events, so the row raced sideways whenever the pointer happened to
   be over it. Telling a trackpad from a wheel mouse by delta size is guesswork
   that fails differently on each platform, so instead the row only ever moves
   when the visitor means it to:

     arrows - a button at each end, shown only when the row overflows and only
              on devices with a mouse pointer
     drag   - click and drag the row, like a map
     native - touch swipe, trackpad horizontal swipe and shift+wheel are all
              untouched and keep working as the browser intends

   Vertical scrolling is never intercepted, so the page always scrolls normally
   no matter where the pointer is. */
(function () {
  var SELECTOR = [
    '.dodo-tabs__track',                    /* Best seller / Viral in Korea / DODO Sets */
    '.dodo-ss__track',                      /* Skin Solutions */
    '.dodo-fb__track',                      /* Featured Brands */
    '.dodo-ex__track',                      /* DODOSKIN Exclusive */
    '.featured-collection__container .grid',/* Time Deal / New Arrivals */
    '.dodo-lbp__grid',                      /* Luxury Beauty Picks (mobile) */
    '[data-hscroll]'                        /* opt-in for anything added later */
  ].join(',');

  var DRAG_THRESHOLD = 5; // px before a click counts as a drag
  var bound = typeof WeakSet === 'function' ? new WeakSet() : null;

  function canScrollX(el) {
    return el.scrollWidth - el.clientWidth > 1;
  }

  /* One card, measured from the live layout so it stays correct whatever the
     card width and gap are in a given section. */
  function stepSize(el) {
    var kids = el.children;
    if (kids.length >= 2) {
      var pitch = kids[1].offsetLeft - kids[0].offsetLeft;
      if (pitch > 0) return pitch;
    }
    return Math.max(120, Math.round(el.clientWidth / 4));
  }

  /* ---- drag ---------------------------------------------------------- */

  function attachDrag(el) {
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startLeft = 0;
    var pointerId = null;

    el.addEventListener('pointerdown', function (e) {
      // Touch already scrolls natively; only a mouse or pen needs help.
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (!canScrollX(el)) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      pointerId = e.pointerId;
    });

    el.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!moved) {
        moved = true;
        try { el.setPointerCapture(pointerId); } catch (err) {}
      }
      el.scrollLeft = startLeft - dx;
      e.preventDefault();
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      try { el.releasePointerCapture(pointerId); } catch (err) {}
      pointerId = null;
    }

    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    // Swallow the click that ends a drag so it does not open the product.
    el.addEventListener('click', function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    el.addEventListener('dragstart', function (e) {
      if (moved) e.preventDefault();
    });
  }

  /* ---- arrows -------------------------------------------------------- */

  function makeArrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'dodo-hs-arrow dodo-hs-arrow--' + (dir < 0 ? 'prev' : 'next');
    b.setAttribute('aria-label', dir < 0 ? 'Scroll left' : 'Scroll right');
    /* No glyph: the arrow is a background image on the button (see
       .dodo-hs-arrow in dodo-header.css), artwork that already includes both
       the circle and the chevron. The aria-label above is what screen readers
       announce. */
    return b;
  }

  function attachArrows(el) {
    var parent = el.parentNode;
    if (!parent || parent.nodeType !== 1) return;
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    var prev = makeArrow(-1);
    var next = makeArrow(1);
    // Absolutely positioned, so they stay out of the parent's flex/grid flow
    // and cannot disturb any section's layout.
    parent.appendChild(prev);
    parent.appendChild(next);

    // 10px inside the product row, matching the design.
    var ARROW_INSET = 10;

    /* Vertically the arrows sit on the middle of the product image, not the
       middle of the whole card — a card is image plus brand, name and price, so
       its centre falls somewhere in the text and the arrows looked low.

       The image's offset is taken from its rect relative to the track's, then
       added to the track's own offsetTop, so the result is in the same
       coordinate space as the arrows' positioned parent. Falls back to the
       track's centre when a row has no product image to measure. */
    function imageCentreOffset() {
      var img = el.querySelector('.product-grid-item__image, .dodo-mdp__media, .dodo-brands__card');
      if (!img) return el.clientHeight / 2;
      var imgRect = img.getBoundingClientRect();
      if (!imgRect.height) return el.clientHeight / 2;
      return (imgRect.top - el.getBoundingClientRect().top) + imgRect.height / 2;
    }

    function place() {
      var top = el.offsetTop + imageCentreOffset();
      prev.style.top = top + 'px';
      next.style.top = top + 'px';

      /* The offset is measured from the row, not from the element the arrows
         are positioned against. Most sections inset their scroller inside that
         parent — Time Deal and New Arrivals give theirs a 20px side margin so
         its cards line up with the page gutter — and a flat CSS `left: 10px`
         put the arrows 10px outside those rows instead of inside them. */
      var left = el.offsetLeft;
      var right = parent.clientWidth - (el.offsetLeft + el.offsetWidth);
      prev.style.left = (left + ARROW_INSET) + 'px';
      next.style.right = (right + ARROW_INSET) + 'px';
    }

    function refresh() {
      var scrollable = canScrollX(el);
      var max = el.scrollWidth - el.clientWidth;
      prev.hidden = !scrollable || el.scrollLeft <= 1;
      next.hidden = !scrollable || el.scrollLeft >= max - 1;
      if (scrollable) place();
    }

    function nudge(dir) {
      var max = el.scrollWidth - el.clientWidth;
      var target = Math.max(0, Math.min(max, el.scrollLeft + dir * stepSize(el)));
      // Not behavior:'smooth' — that is driven by requestAnimationFrame, which
      // browsers suspend in a background tab, and the row would not move.
      el.scrollLeft = target;
      refresh();
    }

    prev.addEventListener('click', function (e) { e.preventDefault(); nudge(-1); });
    next.addEventListener('click', function (e) { e.preventDefault(); nudge(1); });
    el.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
    // Card images change the row's height as they load, so re-place once they
    // have settled.
    setTimeout(refresh, 800);

    /* Re-place whenever the row or the box the arrows are positioned against
       changes size. Some sections put a lazy-loaded banner above the track
       inside that same box — DODOSKIN Exclusive and Featured Brands both do —
       and the banner is below the fold, so it loads long after the timeout
       above. Until it does, the track sits higher than it eventually will and
       the arrows are placed against that stale position, which is why they
       looked right after a reload or a resize but wrong on first view.

       This cannot loop: the arrows are absolutely positioned, so moving them
       never changes the size of anything being observed. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { refresh(); });
      ro.observe(el);
      ro.observe(parent);
    }

    /* And directly when an image inside the box finishes loading. This covers
       the same case as the observer above and is what actually fires first for
       a lazy banner; `load` does not bubble, hence the capture phase. */
    parent.addEventListener('load', function (e) {
      if (e.target && e.target.tagName === 'IMG') refresh();
    }, true);
  }

  function bind(el) {
    if (!el || (bound && bound.has(el))) return;
    if (bound) bound.add(el);
    else if (el.dataset.ddHscrollBound) return;
    else el.dataset.ddHscrollBound = '1';

    attachDrag(el);
    attachArrows(el);
  }

  function scan() {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) bind(nodes[i]);
  }

  function start() {
    scan();
    var ticks = 0;
    var poll = setInterval(function () {
      scan();
      if (++ticks >= 6) clearInterval(poll);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Re-bind when a section is re-rendered in the theme editor, and when a
  // tabbed panel's products arrive from dodo-panels.js.
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('dodo:panel:loaded', scan);
})();
